import type { ChallengeInfo } from '@accra/shared';
import { fetchChallenge, createChallengeCode, fetchMyChallenges } from '../net/challenges';
import { fetchBoard } from '../net/leaderboards';
import type { Menu } from '../ui/screens/Menu';
import { copyLink } from '../ui/share/shareLink';
import { challengeCodeFromUrl, challengeUrl, clearChallengeFromUrl } from './challengeLink';
import type { Rival } from './runPlan';

/** The daily challenge board, friend challenge links, and sharing a shift. App just calls into this. */
export class ChallengeFlow {
  /** A friend's challenge waiting to be accepted from the menu. */
  friend: ChallengeInfo | null = null;

  constructor(private readonly menu: Menu, private readonly myName: () => string | null) {}

  /** If the page was opened from a challenge link (?c=…), shows it on the menu. */
  async openLink(): Promise<void> {
    const code = challengeCodeFromUrl(location.search);
    if (!code) return;
    clearChallengeFromUrl();
    this.friend = await fetchChallenge(code);
    this.menu.setChallenge(this.friend);
  }

  /** Updates the red number on the Me button. */
  async refreshUnseen(key: string | null): Promise<void> {
    this.menu.setUnseen((await fetchMyChallenges(key))?.unseen ?? 0);
  }

  dismiss(): void {
    this.friend = null;
    this.menu.setChallenge(null);
  }

  /** Today's top three, shown on the menu card. */
  async refreshDaily(): Promise<void> {
    this.menu.setDailyBoard(await fetchBoard('challenge'));
  }

  /** The player to chase in the daily challenge: today's leader, if that isn't you. */
  async dailyRival(): Promise<Rival | null> {
    const top = (await fetchBoard('challenge'))?.[0];
    return top && top.name !== this.myName() ? { name: top.name, score: top.score } : null;
  }

  /** Copies the link for a challenge you already sent. */
  copy(code: string): Promise<'copied' | 'failed'> {
    return copyLink(challengeUrl(code));
  }

  /** Makes a link for a finished shift and copies it to the clipboard. The link is returned too, to show on screen. */
  async share(key: string | null, runId: string | null): Promise<{ result: 'copied' | 'failed' | 'unavailable'; url?: string }> {
    const code = runId ? await createChallengeCode(key, runId) : null;
    if (!code) return { result: 'unavailable' };
    const url = challengeUrl(code);
    return { result: await copyLink(url), url };
  }
}
